import { Injectable } from '@angular/core';
import { CapacitorSQLite, SQLiteConnection } from '@capacitor-community/sqlite';
import type { SQLiteDBConnection } from '@capacitor-community/sqlite';
import { SqliteObjectWrapper } from './sqlite-object-wrapper';

@Injectable({ providedIn: 'root' })
export class SqliteWrapperService {
  private sqliteConnection = new SQLiteConnection(CapacitorSQLite);
  private openConnections = new Map<string, SqliteObjectWrapper>();
  private pendingConnections = new Map<string, Promise<SqliteObjectWrapper>>();

  async create(options: { name: string; location: string }): Promise<SqliteObjectWrapper> {
    const dbName = options.name.endsWith('.db')
      ? options.name.slice(0, -3)
      : options.name;

    if (this.openConnections.has(dbName)) {
      return this.openConnections.get(dbName);
    }

    if (this.pendingConnections.has(dbName)) {
      return this.pendingConnections.get(dbName);
    }

    const connectionPromise = this.openConnection(dbName);
    this.pendingConnections.set(dbName, connectionPromise);

    try {
      return await connectionPromise;
    } finally {
      this.pendingConnections.delete(dbName);
    }
  }

  private async openConnection(dbName: string): Promise<SqliteObjectWrapper> {
    try {
      // Tras un location.reload la conexión sigue abierta en nativo pero el JS ya no la conoce, y
      // createConnection falla con "already exists": esto cierra esas conexiones huérfanas
      const isConsistent = (await this.sqliteConnection.checkConnectionsConsistency()).result;
      if (!isConsistent) this.openConnections.clear();

      const isConn = (await this.sqliteConnection.isConnection(dbName, false)).result;
      let db: SQLiteDBConnection;
      if (isConn) {
        db = await this.sqliteConnection.retrieveConnection(dbName, false);
      } else {
        try {
          db = await this.sqliteConnection.createConnection(dbName, false, 'no-encryption', 1, false);
        } catch (createError) {
          // Race condition: another process created the connection between our isConnection check and createConnection
          db = await this.sqliteConnection.retrieveConnection(dbName, false);
        }
      }

      await db.open();
      const wrapper = new SqliteObjectWrapper(db);
      this.openConnections.set(dbName, wrapper);
      return wrapper;
    } catch (error) {
      console.error('[SqliteWrapperService] Error creating connection:', dbName, error);
      throw error;
    }
  }

  async migrateOldDatabases(): Promise<void> {
    try {
      const timeout = new Promise<void>(resolve => setTimeout(resolve, 5000));
      await Promise.race([this.sqliteConnection.addSQLiteSuffix(), timeout]);
    } catch (error) {
      console.error('[SqliteWrapperService] migration error:', error);
    }
  }

  async closeAll(): Promise<void> {
    for (const [dbName] of this.openConnections) {
      await this.sqliteConnection.closeConnection(dbName, false).catch(() => {});
    }
    this.openConnections.clear();
  }
}
